/**
 * @file Provides uploading capability in the frontend.
 *
 * Will try resumable uploads (direct to Google Cloud Storage) first, then
 * fallback to Beethoven uploads.
 *
 */
import { trackEvent } from '@@/bits/analytics'
import { customAjax, type CustomXHR } from '@@/bits/custom_ajax'
import { captureMessage } from '@@/bits/error_tracker'
import { getEmbeddedUrl, getName } from '@@/bits/file_handler'
import type { GCSResumableUpload } from '@@/bits/gcs_resumable_upload'
import {
  cancelResumableUpload,
  createResumableUpload,
  INVALID_RESUMABLE_SESSION_URI,
  startResumableUpload,
  subscribeUploadEvent,
  supportsGcsResumableUploads,
} from '@@/bits/gcs_resumable_upload'
import { defer } from 'es-toolkit/compat'
import EventEmitter from 'events'

import { ONE_MB } from '@@/bits/numbers'

let uploadsInProgress: UploadJob[] = []
const FILE_TOO_LARGE_ERROR = 'FILE_TOO_LARGE_ERROR'
const VIDEO_TRIM_FAILED_ERROR = 'VIDEO_TRIM_FAILED_ERROR'
const MAX_FILE_UPLOAD_BYTES_SIZE = 31457280

const getErrorCode = (responseData: XMLHttpRequest): string | undefined => {
  if (typeof responseData.responseText !== 'string' || responseData.responseText === '') return

  try {
    const parsedResponse = JSON.parse(responseData.responseText) as { code?: unknown }
    return typeof parsedResponse.code === 'string' ? parsedResponse.code : undefined
  } catch {
    // A malformed response has no usable error code; preserve the original server error.
    return
  }
}

const trackError = function (label: string, context: Record<string, unknown> = {}): void {
  trackEvent('Uploads', 'Upload failed', label)
  context.fileType = label
  captureMessage('UploadError', { context })
}

const trackSuccess = function (label: string): void {
  trackEvent('Uploads', 'Upload succeeded', label)
}

type UploadPurpose = 'video_recording' | 'audio_recorder' | 'comment_attachment'

type ClassroomKind =
  | 'announcement'
  | 'discussion'
  | 'assignment'
  | 'announcementCreate'
  | 'discussionCreate'
  | 'assignmentCreate'

interface UploadOwnerParams {
  wallId?: number
  libraryId?: number
  tenantId?: number
  classroomKind?: ClassroomKind
  classroomId?: string | number
}

interface UploadOptions extends UploadOwnerParams {
  maxFileSize?: number
  uploadPurpose?: UploadPurpose
  recordingDuration?: number
  useExpiringStorage?: boolean
  makePublic?: boolean
  destinationPrefix?: string
  trimVideoAfterUpload?: {
    start: number
    end: number
    trimToken?: string
    outputMimeType?: string
  }
}

const compactUploadOwnerParams = (params: UploadOwnerParams): Record<string, number | boolean | string> => {
  const compacted: Record<string, number | boolean | string> = {}
  if (params.wallId != null) compacted.wallId = params.wallId
  if (params.libraryId != null) compacted.libraryId = params.libraryId
  if (params.tenantId != null) compacted.tenantId = params.tenantId
  if (params.classroomKind != null && params.classroomId != null) {
    compacted.classroomKind = params.classroomKind
    compacted.classroomId = params.classroomId
  }
  return compacted
}

const classroomUploadOwner = (
  kind: ClassroomKind,
  id: string | number | null | undefined,
): UploadOwnerParams => (id == null || id === '' ? {} : { classroomKind: kind, classroomId: id })

interface ProgressEvent {
  loaded: number
  total: number
}

interface UploadAttributes {
  url: string
  fields: Record<string, string>
  resumable?: boolean
}

class UploadJob extends EventEmitter {
  file: File
  options: UploadOptions

  private xhr1?: CustomXHR
  private xhr2?: CustomXHR
  private completionXhr?: CustomXHR
  private trimXhr?: CustomXHR
  private resumableUpload?: GCSResumableUpload

  constructor(file: File, options: UploadOptions = {}) {
    super()
    this.file = file
    this.options = options
  }

  get shouldSkipFileSizeCheck(): boolean {
    return (
      this.options.useExpiringStorage ||
      this.uploadPurpose === 'audio_recorder' ||
      this.uploadPurpose === 'video_recording'
    )
  }

  get maxFileSize(): number | undefined {
    if (this.options.maxFileSize != null) {
      // We give 1MB extra. Psychologically, 25.5MB is 25MB, not 26MB.
      return this.options.maxFileSize + ONE_MB
    }
    return this.options.maxFileSize
  }

  get uploadPurpose(): UploadPurpose | '' {
    return this.options.uploadPurpose ?? ''
  }

  get recordingDuration(): number | undefined {
    return this.options.recordingDuration
  }

  get wallId(): number | undefined {
    return this.options.wallId
  }

  get libraryId(): number | undefined {
    return this.options.libraryId
  }

  get tenantId(): number | undefined {
    return this.options.tenantId
  }

  get classroomKind(): ClassroomKind | undefined {
    return this.options.classroomKind
  }

  get classroomId(): string | number | undefined {
    return this.options.classroomId
  }

  cancel(): void {
    if (this.xhr1 != null) this.xhr1.abort()
    if (this.xhr2 != null) this.xhr2.abort()
    if (this.resumableUpload != null) cancelResumableUpload(this.resumableUpload)
    if (this.completionXhr != null) this.completionXhr.abort()
    if (this.trimXhr != null) this.trimXhr.abort()
    this.emit('cancel')
  }

  perform(): void {
    if (this.file === undefined) {
      captureMessage('UploadError file is undefined', {
        context: { file: this.file, options: this.options },
      })
      const errorMessage = 'File not found'
      this._reject({ error: errorMessage })
      return
    }

    /**
     * File Size Validation Logic:
     *
     * For audio/video recordings (uploadPurpose: 'audio_recorder' | 'video_recording'):
     * - Skip file size validation entirely
     * - Recording duration is already enforced by the UI (recorder stops at max time)
     * - File size varies based on quality, compression, and content complexity
     * - A 2-minute video could be 50MB or 200MB depending on these factors
     *
     * For regular file uploads:
     * - Validate against maxFileSize limit
     */
    const maxFileSizeBytes = this.maxFileSize

    if (!this.shouldSkipFileSizeCheck && maxFileSizeBytes != null && this.file.size > maxFileSizeBytes) {
      trackEvent('Uploads', 'Upload failed due to big filesize', this.fileType, this.file.size)
      this._reject({ error: FILE_TOO_LARGE_ERROR })
    } else {
      // Sometimes, a file can just be a holder of a url. (e.g. gdoc file)
      // It so, try to get the URL and just return that.
      getEmbeddedUrl(this.file).then(this._resolve.bind(this), this._ajax1.bind(this, this._useResumable()))
    }
  }

  _progress(e: ProgressEvent, _timeStart: Date): void {
    const loadRatio = e.loaded / e.total
    // const timeNow = new Date()
    // const timeElapsed = timeNow - timeStart
    // const expectedTimeToProcess = 3000 //3 seconds
    // const loadRatio = e.loaded / e.total
    // const expectedTimeToUpload = timeElapsed / loadRatio
    // const expectedTimeToUploadAndProcess = expectedTimeToUpload + expectedTimeToProcess;
    // const actualProgress = timeElapsed/expectedTimeToUploadAndProcess
    this.emit('progress', loadRatio)
  }

  _useResumable(): boolean {
    return supportsGcsResumableUploads()
  }

  _resolve(obj: unknown): void {
    this.emit('done', obj)
  }

  _reject(obj: unknown): void {
    this.emit('error', obj)
  }

  // We will request to do resumable uploads but the backend
  // can override that decision.
  _ajax1(requestResumableEndpoint: boolean = false): void {
    const uploadAttributesParams = Object.fromEntries(
      Object.entries({
        unique: +new Date(),
        fileSize: this.file.size,
        filename: this.file.name,
        contentType: this.fileType,
        resumable: requestResumableEndpoint,
        maxFileSize: this.maxFileSize,
        uploadPurpose: this.uploadPurpose,
        recordingDuration: this.recordingDuration,
        useExpiringStorage: this.options.useExpiringStorage ? 'true' : undefined,
        makePublic: this.options.makePublic ? 'true' : undefined,
        destinationPrefix: this.options.destinationPrefix,
        ...compactUploadOwnerParams(this.options),
      }).filter(([_, value]) => value != null), // only include defined values
    )

    this.xhr1 = customAjax({
      url: '/media/upload_attributes.json',
      dataType: 'json',
      type: 'GET',
      cache: false,
      sendOauthToken: true,
      data: uploadAttributesParams,
      error: (responseData, textStatus) => {
        if (textStatus === 'abort') return
        trackError('ajax1', {
          statusCode: responseData.status,
          readyState: responseData.readyState,
        })
        this._reject(responseData)
      },
      success: (attributes: UploadAttributes): void => {
        const { resumable } = attributes
        if (resumable === true) {
          this._resumable(attributes)
        } else {
          this._ajax2(attributes)
        }
      },
    })
  }

  _ajax2(attributes: UploadAttributes): void {
    const timeStart = new Date()
    const formData = new FormData()
    const name = getName(this.file)
    const originalFilename = this.file.name !== '' ? this.file.name : name

    const fieldsOverrides: Record<string, any> = {}
    if (this.options.maxFileSize != null && this.options.maxFileSize > 0) {
      fieldsOverrides.max_file_size = this.options.maxFileSize + 1048576
      fieldsOverrides.transcode = true
    }
    const fields = Object.assign({}, attributes.fields, fieldsOverrides)
    Object.entries(fields).forEach((x) => {
      if (x[0] !== 'key') formData.append(x[0], x[1])
    })
    formData.append('key', [attributes.fields.key, name].join('/'))
    formData.append('Content-Type', this.fileType)
    formData.append('file_size', String(this.file.size))
    formData.append('title', originalFilename)
    formData.append('file', this.file, name)
    formData.append('uploadPurpose', this.uploadPurpose)
    if (this.shouldTrimVideoAfterUpload) {
      const trimOptions = this.options.trimVideoAfterUpload!
      formData.append('trim_start', String(trimOptions.start))
      formData.append('trim_end', String(trimOptions.end))
    }

    this.xhr2 = customAjax({
      url: attributes.url,
      crossDomain: true,
      type: 'POST',
      data: formData,
      cache: false,
      contentType: false,
      processData: false,
      dataType: 'json',
      xhr: () => {
        const xhr = new XMLHttpRequest()
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            this._progress(this.shouldTrimVideoAfterUpload ? { loaded: e.loaded * 0.5, total: e.total } : e, timeStart)
          }
        })
        return xhr
      },
      error: (responseData, textStatus) => {
        if (textStatus === 'abort') return
        trackError(this.fileType, {
          statusCode: responseData.status,
          userId: fields.uploader_id,
          filename: this.file.name,
          filsSize: this.file.size,
          maxFileSize: fields.max_file_size ?? 'None',
          resuamble: false,
          readyState: responseData.readyState,
          responseText: responseData.responseText,
          responseXml: responseData.responseXML,
        })
        if (this.shouldTrimVideoAfterUpload && getErrorCode(responseData) === VIDEO_TRIM_FAILED_ERROR) {
          this._reject({
            error: VIDEO_TRIM_FAILED_ERROR,
            status: responseData.status,
            statusText: 'Video trimming failed',
            originalError: responseData,
          })
          return
        }
        this._reject(responseData)
      },
      success: (responseData) => {
        if (this.shouldTrimVideoAfterUpload) {
          this._progress({ loaded: 1, total: 1 }, timeStart)
        }
        trackSuccess(this.fileType)
        this._resolve(responseData)
      },
    })
  }

  _resumable(attributes: UploadAttributes): void {
    const timeStart = new Date()
    const file = this.file
    const { fields } = attributes
    const { completion_url: completionUrl } = fields

    // Standard way of handling error and falling back
    const trackErrorAndFallback = (error: Error): void => {
      trackError(this.fileType, {
        action: 'gcsResumableUpload',
        message: error.message,
        type: 'GCS Resumable Upload',
        fileSize: file.size,
        filename: file.name,
        userId: fields.uploader_id,
        maxFileSize: fields.max_file_size != null ? fields.max_file_size : 'None',
      })

      // Fallback to the older way
      this._ajax1(false)
    }

    // 1. Check that resumable URL is successfully created
    const sanityCheck = (gru: GCSResumableUpload): GCSResumableUpload => {
      const { resumableSessionUri } = gru
      // Check if we initiated the session successfully
      if (
        resumableSessionUri == null ||
        resumableSessionUri === '' ||
        resumableSessionUri === INVALID_RESUMABLE_SESSION_URI
      ) {
        throw new Error('Cannot perform resumable upload')
      }

      // Assign for later user e.g. when cancelling the upload
      this.resumableUpload = gru
      return gru
    }

    // 2. Subscribe to events. Know when things fail, when there's progress, when an
    // upload is complete.
    const subscribeToUploadEvents = (gru: GCSResumableUpload): GCSResumableUpload => {
      subscribeUploadEvent(gru, 'error', (_gru, error) => {
        trackErrorAndFallback(error)
      })

      subscribeUploadEvent(gru, 'progress', (_gru, fractionPercentage) => {
        if (this.shouldTrimVideoAfterUpload) {
          // If we're going to trim, scale upload progress to only 50%.
          // We allocate the other 50% for the trim operation.
          this._progress({ loaded: fractionPercentage * 0.5, total: 1.0 }, timeStart)
        } else {
          // Normal progress reporting for non-video trimming uploads
          this._progress({ loaded: fractionPercentage, total: 1.0 }, timeStart)
        }
      })

      subscribeUploadEvent(gru, 'completion', async () => {
        if (this.shouldTrimVideoAfterUpload) {
          const trimOptions = this.options.trimVideoAfterUpload!
          // If we need to trim the video, we do so after the upload is complete.
          // We simulate the progress of the trim operation to give the user feedback.
          const { timer, cleanup } = this._simulateTrimProgress(timeStart)

          try {
            trackEvent('Uploads', 'Trim video after upload', fields.uploaded_url, null, {
              fileSize: this.file.size,
              fileType: this.fileType,
              fileName: this.file.name,
              trimStart: trimOptions.start,
              trimEnd: trimOptions.end,
            })
            // NOTE: this is better as an async operation.
            const trimResult = await this._trimVideoAfterUpload(fields.uploaded_url, {
              start: trimOptions.start,
              end: trimOptions.end,
              trimToken: fields.trim_token,
            })

            cleanup()

            // Video is trimmed and transcoded so url or extension may differ from original upload
            fields.uploaded_url = trimResult.url
            fields.filename = trimResult.filename
          } catch (error: unknown) {
            clearInterval(timer)

            const errorMessage = error instanceof Error ? error.message : 'Unknown error'
            const errorStatus = error instanceof Error && 'status' in error ? error.status : undefined

            trackError(this.fileType, {
              action: 'videoTrimAfterUpload',
              message: errorMessage,
              type: 'Video Trim After Upload',
              fileSize: file.size,
              filename: file.name,
              userId: fields.uploader_id,
              maxFileSize: fields.max_file_size != null ? fields.max_file_size : 'None',
              statusCode: errorStatus,
              trimStart: trimOptions.start,
              trimEnd: trimOptions.end,
            })

            this._reject({
              error: VIDEO_TRIM_FAILED_ERROR,
              status: errorStatus,
              statusText: 'Video trimming failed',
              originalError: error,
            })
            return // Don't continue to _completeResumable
          }
        }

        // Skip completion call for expiring storage.
        // Files uploaded to expiring storage bucket are meant to be temporary and don't need further processing.
        if (this.options.useExpiringStorage) {
          trackSuccess(this.fileType)
          this._resolve({ url: fields.uploaded_url, filename: fields.filename })
        } else {
          this._completeResumable(completionUrl, fields)
        }
      })
      return gru
    }

    createResumableUpload(file, attributes as any)
      .then(sanityCheck)
      .then(subscribeToUploadEvents)
      .then(startResumableUpload)
      .catch(trackErrorAndFallback)
  }

  _completeResumable(completionUrl: string, fields: Record<string, unknown>): void {
    const file = this.file
    this.completionXhr = customAjax({
      url: completionUrl,
      crossDomain: true,
      dataType: 'json',
      type: 'POST',
      cache: false,
      data: {
        completeResumable: true,
        ...fields,
      },
      error: (responseData, textStatus) => {
        if (textStatus === 'abort') return
        trackError(this.fileType, {
          action: 'completeResumable',
          userId: fields.uploader_id,
          filename: file.name,
          maxFileSize: fields.max_file_size != null ? (fields.max_file_size as string) : 'None',
          fileSize: file.size,
          statusCode: responseData.status,
          readyState: responseData.readyState,
        })
        this._ajax1(false)
      },
      success: (responseData) => {
        this._resolve(responseData)
        trackSuccess(this.fileType)
      },
    })
  }

  get fileType(): string {
    if (this.file == null) {
      trackError('missing_file', {
        context: {
          uploadOptions: this.options,
          stack: new Error().stack,
        },
      })
      return 'application/octet-stream'
    }
    return this.file.type ?? 'application/octet-stream'
  }

  // #region Video trimming
  async _trimVideoAfterUpload(
    uploadedUrl: string,
    trimOptions: {
      start: number
      end: number
      trimToken: string
      outputMimeType?: string
    },
  ): Promise<{ url: string; filename: string }> {
    return await new Promise((resolve, reject) => {
      this.trimXhr = customAjax({
        url: `/beethoven-v2/video/trim`,
        dataType: 'json',
        type: 'POST',
        headers: {
          Authorization: `Bearer ${trimOptions.trimToken}`,
        },
        data: {
          url: uploadedUrl,
          start: trimOptions.start,
          end: trimOptions.end,
          outputMimeType: trimOptions.outputMimeType,
        },
        cache: false,
        timeout: 5 * 60 * 1000, // 5 minutes
        error(responseData) {
          reject(responseData)
        },
        success(responseData) {
          resolve(responseData)
        },
      })
    })
  }

  _simulateTrimProgress(timeStart: Date): { timer: ReturnType<typeof setInterval>; cleanup: () => void } {
    let trimProgress = 0
    const BASE_FILE_SIZE = 25 * 1024 * 1024 // 25MB, roughly corresponds to 1m of video
    const BASE_DURATION = 30000 // 30 seconds base duration
    const DURATION_STEP = 10000 // 10 second increase per size tier
    const SIZE_MULTIPLIERS = [2, 3, 5, 8, 13, 21] // Fibonacci-like sequence. Values from experimentation roughly follows this.

    // Find the appropriate duration based on file size
    const fileSize = this.file.size
    let expectedTrimDuration = BASE_DURATION

    for (let i = 0; i < SIZE_MULTIPLIERS.length; i++) {
      if (fileSize > BASE_FILE_SIZE * SIZE_MULTIPLIERS[i]) {
        expectedTrimDuration += DURATION_STEP
      } else {
        break
      }
    }
    const progressInterval = 500 // Update every 500ms
    const progressIncrement = 0.5 / (expectedTrimDuration / progressInterval)

    const progressTimer = setInterval(() => {
      trimProgress += progressIncrement
      // Ensure we don't exceed 100% (50% upload + 50% trim)
      const totalProgress = Math.min(0.5 + trimProgress, 1.0)
      this._progress({ loaded: totalProgress, total: 1.0 }, timeStart)
    }, progressInterval)

    return {
      timer: progressTimer,
      cleanup: () => {
        clearInterval(progressTimer)
        // Ensure we show 100% completion
        this._progress({ loaded: 1.0, total: 1.0 }, timeStart)
      },
    }
  }

  get isVideoFile(): boolean {
    return this.fileType.startsWith('video/')
  }

  get shouldTrimVideoAfterUpload(): boolean {
    return this.isVideoFile && this.options.trimVideoAfterUpload != null
  }
  // #endregion
}

const startUpload = function (file: File, options: UploadOptions = {}): UploadJob {
  const uploadJob = new UploadJob(file, options)
  uploadJob.on('done', (_result) => {
    uploadsInProgress = uploadsInProgress.filter((x) => x !== uploadJob)
  })
  uploadJob.on('error', (_error) => {
    uploadsInProgress = uploadsInProgress.filter((x) => x !== uploadJob)
  })
  uploadJob.on('cancel', (_error) => {
    uploadsInProgress = uploadsInProgress.filter((x) => x !== uploadJob)
  })
  uploadsInProgress.push(uploadJob)
  defer(() => uploadJob.perform())
  return uploadJob
}

const stopUpload = function (file: File): void {
  const uploadJob = uploadsInProgress.find((x) => x.file === file)
  if (uploadJob != null) {
    uploadJob.cancel()
  }
}

export {
  classroomUploadOwner,
  compactUploadOwnerParams,
  FILE_TOO_LARGE_ERROR,
  MAX_FILE_UPLOAD_BYTES_SIZE,
  startUpload,
  stopUpload,
  UploadJob,
  VIDEO_TRIM_FAILED_ERROR,
}
export type { ClassroomKind, UploadOwnerParams, UploadPurpose, UploadOptions }
